import React, { useState, useRef, useContext, useEffect } from "react";
import "./CircleCompound.css";
import { formatter, formatter2 } from "../../../../utils/helpers";
import { translations } from "../../../../utils/lang";
import { Context } from "../../../../context/Context";

function getColor(value, maxValue) {
  const ratio = maxValue > 0 ? value / maxValue : 0;
  const start = [255, 255, 255];
  const end = [100, 70, 114];
  const r = Math.round(start[0] + (end[0] - start[0]) * ratio);
  const g = Math.round(start[1] + (end[1] - start[1]) * ratio);
  const b = Math.round(start[2] + (end[2] - start[2]) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function getTextColor(rgb) {
  // extract numbers from "rgb(r, g, b)"
  const [r, g, b] = rgb.match(/\d+/g).map(Number);

  // relative luminance (WCAG-ish)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.6 ? "#000" : "#fff";
}

function ValueCircles({ top, bottom, maxValue, isPercentage, showAverage }) {
  const background = getColor(bottom, maxValue);
  const textColor = getTextColor(background);
  const average = top && bottom && top !== 0 ? bottom / top : 0;

  const circleStyle = {
    background,
    color: textColor,
  };

  return (
    <div className="circle-wrapper">
      <div className="circle-line"></div>

      {/* Top circle (count / percent) */}
      <div
        className="circle-small"
        title={`${isPercentage ? "% " : "count "} ${top}`}
        style={circleStyle}
      >
        {formatter2.format(top)}
        {isPercentage ? "%" : ""}
      </div>

      {/* Bottom circle (amount) */}
      <div
        className="circle-large"
        title={`Amount ${formatter.format(bottom)}`}
        style={circleStyle}
      >
        {formatter2.format(bottom)}
      </div>

      {/* Optional average circle */}
      {showAverage && top > 0 && (
        <div
          className="circle-small circle-average"
          title={`Avg ${formatter2.format(average)}`}
          style={circleStyle}
        >
          {formatter2.format(average)}
        </div>
      )}
    </div>
  );
}

export default function MonthlySummaryTable({ data, daily }) {
  const { lang } = useContext(Context);
  const years = Object.keys(data).sort();
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1]);
  const dropdownRef = useRef(null);
  const monthRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);

  useEffect(() => {
    setSelectedYear(years[years.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  let maxAmount = 0;
  Object.values(data).forEach((yearObj) => {
    Object.values(yearObj).forEach((monthObj) => {
      Object.values(monthObj).forEach((metric) => {
        if (metric.amount && metric.amount > maxAmount)
          maxAmount = metric.amount;
      });
    });
  });

  const metricOrder = [
    "completed",
    "campaign",
    "rejected",
    "canceled",
    "endurance",
    "refund",
    "grandTotal",
  ];

  const monthOrder = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];

  return (
    <div className="circle-compound-container">
      {/* YEAR FILTER */}

      <div className="summary-container">
        <div className="summary-table">
          {/* Header */}
          <div className="row header-row">
            <div className="calendar-header">
              <div
                ref={dropdownRef}
                className={`month-dropdown ${dropdownOpen ? "down" : ""}`}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span className="month-label">{selectedYear}</span>
                <span className="chevron">{dropdownOpen ? "▲" : "▼"}</span>

                {dropdownOpen && (
                  <div className="month-options">
                    {years.map((y) => (
                      <div
                        key={y}
                        className="month-option"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedYear(y);
                          setDropdownOpen(false);
                        }}
                      >
                        {y}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {monthOrder.map((m) => {
              const capitalized = m.charAt(0).toUpperCase() + m.slice(1);
              return (
                <div key={m} className="month-title">
                  {translations[lang].filter.month[capitalized]}
                </div>
              );
            })}

            <div
              ref={monthRef}
              className={`month-dropdown ${monthDropdownOpen ? "down" : ""}`}
              onClick={() => setMonthDropdownOpen(!monthDropdownOpen)}
            >
              <span className="month-label">
                {selectedMonth
                  ? translations[lang].filter.month[
                      selectedMonth.charAt(0).toUpperCase() +
                        selectedMonth.slice(1)
                    ]
                  : "Months"}
              </span>
              <span className="chevron">{monthDropdownOpen ? "▲" : "▼"}</span>

              {monthDropdownOpen && (
                <div className="month-options">
                  {monthOrder.map((m) => {
                    if (!daily[selectedYear] || !daily[selectedYear][m])
                      return null;
                    return (
                      <div
                        key={m}
                        className="month-option"
                        onClick={() => {
                          setSelectedMonth(m);
                          setMonthDropdownOpen(false);
                        }}
                      >
                        {
                          translations[lang].filter.month[
                            m.charAt(0).toUpperCase() + m.slice(1)
                          ]
                        }
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Metrics */}
          {metricOrder.map((metric) => (
            <div key={metric} className="row metric-row">
              <div className="metric-title">
                {translations[lang].compoundMonthly[metric]}
              </div>

              {monthOrder.map((month) => {
                const monthData = data[selectedYear]?.[month];
                if (!monthData || !monthData[metric])
                  return <div key={month}></div>;

                const value = monthData[metric];
                return (
                  <ValueCircles
                    key={month}
                    top={value.count ?? value.percent ?? 0}
                    bottom={value.amount ?? 0}
                    maxValue={maxAmount}
                    isPercentage={value.percent}
                    showAverage={metric === "completed"}
                  />
                );
              })}
              {selectedMonth &&
              [
                "completed",
                "rejected",
                "canceled",
                "endurance",
                "refund",
              ].includes(metric) ? (
                daily[selectedYear]?.[selectedMonth] ? (
                  <ValueCircles
                    key="daily"
                    top={daily[selectedYear][selectedMonth][metric]?.count ?? 0}
                    bottom={
                      daily[selectedYear][selectedMonth][metric]?.amount ?? 0
                    }
                    maxValue={maxAmount}
                    isPercentage={false}
                    showAverage={metric === "completed"}
                  />
                ) : (
                  <div key="daily"></div>
                )
              ) : (
                <div key="daily"></div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
